import { gql } from '@apollo/client'

// Authentication Queries
export const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    me {
      id
      email
      name
      phone_number
      active
    }
  }
`

// User Queries
export const GET_USERS = gql`
  query GetUsers($filters: UserFilters) {
    users(filters: $filters) {
      id
      email
      name
      phone_number
      active
      invite_pending
      created_at
      updated_at
      roles {
        id
        role
        description
      }
    }
  }
`

export const GET_USER = gql`
  query GetUser($id: Int!) {
    user(id: $id) {
      id
      email
      name
      phone_number
      active
      invite_pending
      created_at
      updated_at
      roles {
        id
        role
        description
      }
    }
  }
`

export const GET_USERS_WITH_PAGINATION = gql`
  query GetUsersWithPagination($page: Int, $limit: Int, $filters: UserFilters) {
    usersWithPagination(page: $page, limit: $limit, filters: $filters) {
      users {
        id
        email
        name
        active
        invite_pending
        roles {
          id
          role
          description
        }
      }
      total
      page
      totalPages
    }
  }
`

export const GET_ACTIVE_USERS = gql`
  query GetActiveUsers {
    activeUsers {
      id
      name
    }
  }
`

export const SEARCH_USERS = gql`
  query SearchUsers($partialName: String!) {
    searchUsers(partialName: $partialName) {
      id
      email
      name
      active
      roles {
        id
        role
        description
      }
    }
  }
`

export const GET_USER_STATS = gql`
  query GetUserStats {
    userStats {
      total
      active
      inactive
    }
  }
`

// Role Queries
export const GET_ROLES = gql`
  query GetRoles($filters: RoleFilters) {
    roles(filters: $filters) {
      id
      role
      description
      active
      created_at
      updated_at
    }
  }
`

export const GET_ROLE = gql`
  query GetRole($id: Int!) {
    role(id: $id) {
      id
      role
      description
      active
      created_at
      updated_at
    }
  }
`

export const GET_ROLE_WITH_PERMISSIONS = gql`
  query GetRoleWithPermissions($id: Int!) {
    roleWithPermissions(id: $id) {
      id
      role
      description
      active
      permissions {
        id
        name
        description
        active
      }
    }
  }
`

export const GET_ROLES_WITH_PERMISSIONS = gql`
  query GetRolesWithPermissions($filters: RoleFilters) {
    rolesWithPermissions(filters: $filters) {
      id
      role
      description
      active
      permissions {
        id
        name
        description
        active
      }
    }
  }
`

export const GET_ROLE_STATS = gql`
  query GetRoleStats {
    roleStats {
      total
      active
      inactive
    }
  }
`

// Order Queries

// Lightweight query for the operational orders overview (no order lines).
// The `orders` field is a deprecated alias of `recentOrders` — migrating all
// operational documents to the new field name is tracked in tasks/todo.md.
export const GET_RECENT_ORDERS = gql`
  query GetRecentOrders($filters: OrderFilters) {
    orders(filters: $filters) {
      id
      order_number
      status
      order_type
      order_source
      customer_name
      customer_email
      customer_phone
      customer_phone_access_code
      total_cents
      externally_handled
      created_at
      scheduled_for
      is_preorder
      staffUser {
        id
        name
      }
      payments {
        id
        amount_cents
        payment_method
        status
        transaction_type
      }
      statusTimings {
        time_in_confirmed_seconds
        time_in_preparing_seconds
        time_in_ready_seconds
        confirmed_to_completed_seconds
      }
    }
  }
`

// Dashboard "Planned orders today" widget: dormant SCHEDULED pre-orders due in
// a scheduled_for window, soonest first. Targets the `recentOrders` field
// directly (the deprecated `orders` alias lacks the sort args client-side).
// View-only by owner decision — release to the kitchen is cron-only.
export const GET_PLANNED_ORDERS_TODAY = gql`
  query GetPlannedOrdersToday(
    $filters: OrderFilters
    $sortField: RecentOrdersSortField
    $sortDirection: SortDirection
  ) {
    recentOrders(filters: $filters, sort_field: $sortField, sort_direction: $sortDirection) {
      id
      order_number
      scheduled_for
      customer_name
      total_cents
    }
  }
`

// Delivery board: today's delivery orders across the whole service flow, own and
// platform alike. Targets `recentOrders` directly (not the deprecated `orders`
// alias), so it adds no debt to the migration tracked in tasks/todo.md.
//
// Windowed on scheduled_from/scheduled_to, NEVER date_from/date_to: this is an
// operational board, and a released pre-order placed days earlier falls outside a
// created_at window and would silently vanish from the screen. Unpaid orders are
// excluded server-side by default, so half-finished checkouts never appear.
export const GET_DELIVERY_ORDERS = gql`
  query GetDeliveryOrders(
    $filters: OrderFilters
    $sortField: RecentOrdersSortField
    $sortDirection: SortDirection
  ) {
    recentOrders(filters: $filters, sort_field: $sortField, sort_direction: $sortDirection) {
      id
      order_number
      status
      current_status_since
      scheduled_for
      is_preorder
      # isOwnDelivery / canPrintDeliverySlip test order_type — without it the PC4 bundle
      # header, "Print N" / "Route N" and the per-card Bezorgbon button never render.
      order_type
      customer_name
      customer_phone
      customer_phone_access_code
      delivery_street
      delivery_house_number
      delivery_postcode
      delivery_city
      delivery_instructions
      delivery_fee_cents
      deposit_total_cents
      packaging_fee_cents
      delivered_by
      delivered_by_user {
        id
        name
      }
      platform
      # Where the order was PLACED (with platform) — an own-delivery order can still come
      # from Thuisbezorgd / Uber Eats. Shown as the "Via" chip on the board and map.
      order_source
      # The customer's own tracking link, for the WhatsApp message on own-delivery platform
      # orders. NULL when the order carries no token (see types/order.ts).
      tracking_url
      total_cents
      created_at
    }
  }
`

// Management order browser (order history / research). Server-paginated with an
// exact total; flat-gated on `order.list.all`. No cache typePolicy needed:
// Apollo's default arg-keying stores each (filters, page, sort) combination as
// its own wholly-replaced entry, which is exactly right for offset paging.
export const GET_ALL_ORDERS = gql`
  query GetAllOrders(
    $filters: AllOrdersFilters
    $limit: Int
    $offset: Int
    $sortField: AllOrdersSortField
    $sortDirection: SortDirection
  ) {
    allOrders(
      filters: $filters
      limit: $limit
      offset: $offset
      sort_field: $sortField
      sort_direction: $sortDirection
    ) {
      total
      limit
      offset
      orders {
        id
        order_number
        status
        order_type
        order_source
        platform
        total_cents
        discount_total_cents
        customer_name
        created_at
        scheduled_for
        is_preorder
        externally_handled
        staffUser {
          id
          name
        }
      }
    }
  }
`

/**
 * Per-order charges the POS must add to the total it submits.
 *
 * Statiegeld rides on the variant we already fetch; a per-ORDER fee has nothing
 * to hang on, so it is read here. The query is public (no auth) and returns 0
 * while the fee is unconfigured, so it is safe to call unconditionally.
 */
export const GET_ORDER_CHARGES = gql`
  query PublicOrderCharges($order_type: OrderType) {
    publicOrderCharges(order_type: $order_type) {
      packaging_fee_cents
    }
  }
`

// ── Dashboard aggregation queries ────────────────────────────────────────────

export const GET_ORDER_STATS = gql`
  query GetOrderStats($filters: OrderStatsFilters) {
    orderStats(filters: $filters) {
      # Sales layer (incl-VAT)
      grossRevenueCents
      # Statiegeld — in the till but never revenue. Needed or the waterfall's
      # "Collected" disagrees with the payments report by exactly the deposit.
      depositCents
      # Both fees, gross — sales (grossRevenueCents is goods only). Ungated:
      # users without order.financial.view build their incl-VAT sales from these.
      packagingFeeCents
      deliveryFeeCents
      totalDiscountCents
      # VAT bridge + ex-VAT layer (null without order.financial.view):
      # turnover = sales (goods + both fees); product sales = turnover − delivery;
      # contribution = the PRODUCT margin; the delivery result stands apart.
      outputVatCents
      deliveryFeeExVatCents
      packagingFeeExVatCents
      turnoverExVatCents
      productSalesExVatCents
      platformCommissionCents
      netRevenueCents
      cogsCents
      contributionMarginCents
      contributionMarginPct
      deliveryFeeCommissionCents
      deliveryResultCents
      linesWithoutCogsCount
      # Staff meals (comp) — ex-VAT ingredient cost, not a subsidy
      staffMealCostCents
      staffMealCount
      # Counts
      orderCount
      # Per-channel split (where the order was placed)
      byChannel {
        channel
        displayName
        sortOrder
        orderCount
        grossRevenueCents
        discountCents
        depositCents
        packagingFeeCents
        deliveryFeeCents
        deliveryFeeExVatCents
        packagingFeeExVatCents
        turnoverExVatCents
        productSalesExVatCents
        platformCommissionCents
        netRevenueCents
        cogsCents
        contributionMarginCents
        contributionMarginPct
        deliveryFeeCommissionCents
        deliveryResultCents
      }
      # Per-fulfilment split (how the order reached the customer), each row
      # split per channel again — the Delivery row's per-channel columns.
      # Only the fields the tiles show (FulfilmentSlice); the API slice
      # carries the full waterfall, deliberately not selected.
      byType {
        orderType
        orderCount
        grossRevenueCents
        discountCents
        packagingFeeCents
        deliveryFeeCents
        deliveryFeeExVatCents
        turnoverExVatCents
        productSalesExVatCents
        contributionMarginCents
        deliveryResultCents
        byChannel {
          channel
          displayName
          sortOrder
          orderCount
          grossRevenueCents
          discountCents
          packagingFeeCents
          deliveryFeeCents
          deliveryFeeExVatCents
          turnoverExVatCents
          productSalesExVatCents
          contributionMarginCents
          deliveryResultCents
        }
      }
      # Kitchen + pickup timing (throughput = non-delivery orders only)
      speed {
        avgPrepSeconds
        sampleCount
        avgThroughputSeconds
        throughputSampleCount
      }
      # Own-driver delivery timing
      deliverySpeed {
        orderCount
        platformOrderCount
        sampleCount
        avgDeliverySeconds
        avgOnRoadSeconds
        maxDeliverySeconds
      }
    }
  }
`

export const GET_PRODUCT_SALES_STATS = gql`
  query GetProductSalesStats($filters: ProductStatsFilters) {
    productSalesStats(filters: $filters) {
      topProducts(limit: 10) {
        productName
        variantName
        qtySold
        revenueCents
      }
      topUpgrades(limit: 8) {
        variantName
        count
        revenueCents
      }
      customizationFrequency {
        groupName
        totalCount
        options {
          label
          count
          priceDeltaCents
        }
      }
    }
  }
`

export const GET_ORDERS = gql`
  query GetOrders($filters: OrderFilters) {
    orders(filters: $filters) {
      id
      order_number
      status
      order_type
      delivered_by
      customer_name
      customer_email
      customer_phone
      customer_phone_access_code
      total_cents
      externally_handled
      created_at
      current_status_since
      scheduled_for
      is_preorder
      staffUser {
        id
        name
      }
      consumingEmployee {
        id
        name
      }
      orderLines {
        id
        unit_price_cents
        subtotal_cents
        tax_rate_percentage
        tax_amount_cents
        total_cents
        quantity
        customizations {
          label
          group_name
          price_delta_cents
        }
        customizations_json
        parent_order_line_id
        relationship_type
        relationship_group_id
        sort_order
        productVariant {
          name
          product {
            name
            variants {
              id
            }
          }
        }
        parentOrderLine {
          id
          productVariant {
            name
            product {
              name
              variants {
                id
              }
            }
          }
          customizations {
            label
            group_name
            price_delta_cents
          }
          customizations_json
        }
        childOrderLines {
          id
          productVariant {
            name
            product {
              name
              variants {
                id
              }
            }
          }
          relationship_type
          customizations {
            label
            group_name
            price_delta_cents
          }
          customizations_json
        }
      }
    }
  }
`

export const GET_ORDER = gql`
  query GetOrder($id: Int!) {
    order(id: $id) {
      id
      order_number
      status
      order_type
      customer_name
      customer_phone
      customer_phone_access_code
      customer_email
      # Where the order was placed. Decides the refund path on the detail page: POS / KIOSK
      # sales are refunded on the terminal, WEBSHOP / HUBRISE refunds happen at Mollie or the
      # platform and are only RECORDED here (recordExternalRefund).
      order_source
      platform
      # Delivery block. delivered_by decides whether the address is ours to act on:
      # platform orders never carry one (their couriers deliver), so an address block
      # must not be rendered for them.
      delivered_by
      delivery_street
      delivery_house_number
      delivery_postcode
      delivery_city
      delivery_instructions
      subtotal_cents
      # GROSS cents (incl. VAT), already inside total_cents. Needed so the Order
      # Summary reconciles on delivery orders: subtotal_cents is goods EX-VAT and the
      # summary's tax figure sums order LINES only, so neither one contains the fee.
      delivery_fee_cents
      # Statiegeld — inside total_cents, in no order line and in no tax figure.
      # Without it the Order Summary's Subtotal + Tax + fee no longer reaches Total.
      deposit_total_cents
      packaging_fee_cents
      total_cents
      cogs_total_cents
      margin_cents
      margin_percentage
      has_partial_cogs
      notes
      hubrise_order_id
      externally_handled
      created_at
      current_status_since
      scheduled_for
      is_preorder
      staffUser {
        id
        name
        email
      }
      payments {
        id
        order_id
        amount_cents
        payment_method
        status
        transaction_type
        transaction_id
        processed_at
        created_at
        terminal_id
        access_protocol
        operating_environment
        # 'mollie' / 'uber_eats' / 'thuisbezorgd' on an externally recorded refund row
        provider
        # Action flags - check these when status === 'COMPLETED'
        ask_merchant_signature
        ask_customer_signature
        ask_customer_identification
        print_customer_receipt
      }
      orderLines {
        id
        unit_price_cents
        subtotal_cents
        tax_rate_percentage
        tax_amount_cents
        total_cents
        quantity
        cogs_cents
        margin_cents
        margin_percentage
        notes
        customizations {
          label
          group_name
          price_delta_cents
        }
        customizations_json
        parent_order_line_id
        relationship_type
        relationship_group_id
        sort_order
        productVariant {
          id
          name
          product {
            id
            name
            kind
          }
        }
        parentOrderLine {
          id
          productVariant {
            name
            product {
              name
              variants {
                id
              }
            }
          }
          customizations {
            label
            group_name
            price_delta_cents
          }
          customizations_json
        }
        childOrderLines {
          id
          productVariant {
            name
            product {
              name
              variants {
                id
              }
            }
          }
          relationship_type
          customizations {
            label
            group_name
            price_delta_cents
          }
          customizations_json
        }
      }
    }
  }
`


export const GET_OPTION_GROUP = gql`
  query GetOptionGroup($id: Int!) {
    optionGroup(id: $id) {
      id
      name
    }
  }
`

// Payment Queries
export const PAYMENT_STATUS = gql`
  query PaymentStatus($id: Int!) {
    paymentStatus(id: $id) {
      id
      order_id
      amount_cents
      payment_method
      status
      transaction_id
      processed_at
      created_at
      # Action flags - check these when status === 'COMPLETED'
      ask_merchant_signature
      ask_customer_signature
      ask_customer_identification
      print_customer_receipt
    }
  }
`

export const GET_PAYMENT_NOTIFICATIONS = gql`
  query GetPaymentNotifications($paymentId: Int!) {
    paymentNotifications(payment_id: $paymentId) {
      id
      payment_id
      type
      message
      message_order_number
      status
      failure_code
      created_at
    }
  }
`

export const GET_PENDING_PAYMENTS_BY_TERMINAL = gql`
  query GetPendingPaymentsByTerminal($terminal_id: String!) {
    pendingPaymentsByTerminal(terminal_id: $terminal_id) {
      id
      order_id
      amount_cents
      payment_method
      status
      transaction_type
      transaction_id
      terminal_id
      created_at
      updated_at
      order {
        id
        order_number
        customer_name
        total_cents
      }
    }
  }
`

// Incoming HubRise orders awaiting operator action.
// Polled by ReceivedOrdersContext to drive the indicator + side sheet.
export const GET_RECEIVED_ORDERS = gql`
  query ReceivedOrders {
    orders(filters: { status: RECEIVED }) {
      id
      order_number
      status
      hubrise_order_id
      customer_name
      customer_email
      customer_phone
      customer_phone_access_code
      total_cents
      created_at
    }
  }
`
